import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
    Box,
    Card,
    CardContent,
    Avatar,
    Stack,
    Divider,
    IconButton,
} from "@mui/material";
import {
    Close as CloseIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationOnIcon,
    DateRange as DateRangeIcon,
    Business as BusinessIcon,
    AttachFile as AttachFileIcon,
    AccountBalanceWallet as WalletIcon,
    Receipt as ReceiptIcon,
} from "@mui/icons-material";
import { useGetAdminInsuranceDetailsQuery } from "../../../store/insurance/insuranceApis";
import { baseUrl } from "../../../helpers/constants";
import { handleDateTimeDefault } from "../../../helpers/utils";

const InsuranceDetailsDialog = ({ open, onClose, insuranceId }) => {
    const { data: insuranceDetails, isLoading } = useGetAdminInsuranceDetailsQuery(insuranceId, {
        skip: !insuranceId || !open,
    });

    const formatFileSize = (bytes) => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    const getPaymentStatusColor = (status) => {
        const statusColors = {
            pending: { bg: "#fff3cd", color: "#856404" },
            paid: { bg: "#d4edda", color: "#155724" },
            failed: { bg: "#f8d7da", color: "#721c24" },
            cancelled: { bg: "#f8f9fa", color: "#6c757d" },
        };
        return statusColors[status] || { bg: "#f8f9fa", color: "#6c757d" };
    };

    if (isLoading) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>Insurance Renewal Details</DialogTitle>
                <DialogContent>
                    <Typography>Loading...</Typography>
                </DialogContent>
            </Dialog>
        );
    }

    if (!insuranceDetails) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>Insurance Renewal Details</DialogTitle>
                <DialogContent>
                    <Typography>No details found.</Typography>
                </DialogContent>
            </Dialog>
        );
    }

    const statusColor = getPaymentStatusColor(insuranceDetails.payment_status);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Insurance Renewal Details</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3}>
                    {/* Header with Insurance Number and Payment Status */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h5" fontWeight="bold">
                            {insuranceDetails.insurance_number}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                backgroundColor: statusColor.bg,
                                color: statusColor.color,
                                fontWeight: "medium",
                                padding: "8px 16px",
                                borderRadius: "16px",
                                fontSize: "0.875rem",
                            }}
                        >
                            {insuranceDetails.payment_status?.toUpperCase() || "PENDING"}
                        </Typography>
                    </Box>

                    <Divider />

                    {/* Customer Information */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            >
                                <PersonIcon />
                                Customer Information
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Full Name
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {insuranceDetails.customer_name}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Mobile Number
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {insuranceDetails.mobile}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Email Address
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {insuranceDetails.email}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Pin Code
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {insuranceDetails.pincode}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        Address
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {insuranceDetails.address}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        City
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {insuranceDetails.city}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        State
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {insuranceDetails.state}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Product Information */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Product Information
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={4}>
                                    {insuranceDetails.product?.image_url && (
                                        <Avatar
                                            src={`${baseUrl}${insuranceDetails.product.image_url}`}
                                            alt={insuranceDetails.product.name}
                                            sx={{ width: 80, height: 80 }}
                                            variant="rounded"
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <Typography variant="body2" color="text.secondary">
                                        Product Name
                                    </Typography>
                                    <Typography variant="h6" fontWeight="medium">
                                        {insuranceDetails.product_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Description
                                    </Typography>
                                    <Typography variant="body1">
                                        {insuranceDetails.product?.description || "No description available"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Policy Information */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            >
                                <ReceiptIcon />
                                Policy Information
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Current Policy Number
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {insuranceDetails.policy_number}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Current Expiry Date
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {insuranceDetails.expiry_date
                                            ? new Date(insuranceDetails.expiry_date).toLocaleDateString()
                                            : "N/A"}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        New Validity From
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {insuranceDetails.validity_from
                                            ? new Date(insuranceDetails.validity_from).toLocaleDateString()
                                            : "N/A"}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        New Validity To
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {insuranceDetails.validity_to
                                            ? new Date(insuranceDetails.validity_to).toLocaleDateString()
                                            : "N/A"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Payment Information */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            >
                                <WalletIcon />
                                Payment Information
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Premium Amount
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        ₹{insuranceDetails.premium_amount || 0}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Paid Amount
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        ₹{insuranceDetails.paid_amount || 0}
                                    </Typography>
                                </Grid>
                                {insuranceDetails.transaction_id && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Transaction ID
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {insuranceDetails.transaction_id}
                                        </Typography>
                                    </Grid>
                                )}
                                {insuranceDetails.payment_method && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Payment Method
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {insuranceDetails.payment_method}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Files - Removed file upload functionality */}
                    {/* {(() => {
                        let filesArray = [];
                        try {
                            // Handle both string and array formats
                            if (typeof insuranceDetails.files === "string") {
                                filesArray = JSON.parse(insuranceDetails.files);
                            } else if (Array.isArray(insuranceDetails.files)) {
                                filesArray = insuranceDetails.files;
                            }
                        } catch (error) {
                            console.error("Error parsing files:", error);
                            filesArray = [];
                        }

                        return (
                            filesArray.length > 0 && (
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                        >
                                            <AttachFileIcon />
                                            Attached Files ({filesArray.length})
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {filesArray.map((file, index) => {
                                                // Handle both string URLs and file objects
                                                const fileUrl = typeof file === "string" ? file : file.uri || file.url;
                                                const fileName =
                                                    typeof file === "string"
                                                        ? `File ${index + 1}`
                                                        : file.name || `File ${index + 1}`;

                                                return (
                                                    <Grid item xs={12} sm={6} key={index}>
                                                        <Card variant="outlined" sx={{ p: 2 }}>
                                                            <Stack spacing={1}>
                                                                <Typography variant="body2" fontWeight="medium">
                                                                    {fileName}
                                                                </Typography>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    href={fileUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    View File
                                                                </Button>
                                                            </Stack>
                                                        </Card>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            )
                        );
                    })()} */}

                    {/* Notes */}
                    {insuranceDetails.notes && (
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Additional Notes
                                </Typography>
                                <Typography variant="body1">{insuranceDetails.notes}</Typography>
                            </CardContent>
                        </Card>
                    )}

                    {/* Application Information */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            >
                                <DateRangeIcon />
                                Application Information
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Application Date
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {handleDateTimeDefault(insuranceDetails.created_at)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Last Updated
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {handleDateTimeDefault(insuranceDetails.updated_at)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InsuranceDetailsDialog;
